import React, { forwardRef, Ref, useContext } from 'react';
import {
  FormGroup,
  KeyboardDetectionContext,
  VisuallyHidden,
  semanticColors,
  cssColorValue,
  addColorSet,
  spacer16,
  spacer32,
} from '@spotify-internal/encore-web';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/core';
import { FieldController } from 'client/shared/FieldController';
import {
  FieldRadioToggleProps,
  ControlledFieldRadioToggleProps,
} from './types';

const BORDER_RADIUS = 100;
const FOCUS_BORDER_SIZE = 3;

const RadioGroup = styled.div`
  display: flex;
  border-radius: ${BORDER_RADIUS}px;
  box-shadow: inset 0 0 0 1px ${cssColorValue(semanticColors.decorativeSubdued)};
`;

const fadeIn = keyframes`
0% { opacity: 0; }
100% { opacity: 1 }
`;

const Radio = styled.label<{ isUsingKeyboard?: boolean }>`
  color: ${cssColorValue(semanticColors.backgroundBase)};
  border-radius: ${BORDER_RADIUS}px;
  margin: 0;
  padding: ${spacer16} ${spacer32};
  cursor: pointer;
  position: relative;

  input:checked + & {
    color: ${cssColorValue(semanticColors.textBase)};
    background: ${cssColorValue(semanticColors.backgroundBase)};
  }

  ${({ isUsingKeyboard }) =>
    isUsingKeyboard &&
    css`
      input:focus + & {
        &:after {
          position: absolute;
          top: -${FOCUS_BORDER_SIZE * 2}px;
          right: -${FOCUS_BORDER_SIZE * 2}px;
          bottom: -${FOCUS_BORDER_SIZE * 2}px;
          left: -${FOCUS_BORDER_SIZE * 2}px;
          border: ${FOCUS_BORDER_SIZE}px solid
            ${cssColorValue(semanticColors.backgroundPress)};
          border-radius: ${BORDER_RADIUS}px;
          animation: ${fadeIn} 200ms linear;
          content: '';
        }
      }
    `}
`;

const HorizontalFormGroup = styled(FormGroup)`
  display: flex;
  align-items: center;
`;

// We use our own label here (instead of the FormGroup's "label" property) simply
// because the underlying LabelGroup has some padding that makes it impossible to
// horizontally align things. This is silly and should be handled in the todo.
// TODO https://anchorfm.atlassian.net/browse/WHEEL-832: Write a better way to
// horizontally align form elements.
const FormLabel = styled.label`
  flex: 1;
  padding: 0;
`;

export const FieldRadioToggle = forwardRef(
  (
    {
      label,
      name,
      options,
      disabled,
      onChange,
      value,
      containerClassName,
      ...rest
    }: FieldRadioToggleProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const { isUsingKeyboard } = useContext(KeyboardDetectionContext);

    return (
      <HorizontalFormGroup className={containerClassName}>
        {label && <FormLabel>{label}</FormLabel>}
        <RadioGroup>
          {options.map((option, index) => {
            const { value: optionValue, label: optionLabel } = option;
            const id = `${name}_${index}`;
            return (
              <div className={addColorSet('mutedAccent')}>
                <VisuallyHidden
                  {...rest}
                  component="input"
                  type="radio"
                  ref={ref}
                  id={id}
                  name={name}
                  disabled={disabled}
                  onChange={disabled ? () => null : onChange}
                  value={optionValue}
                  checked={value === optionValue}
                />
                <Radio htmlFor={id} isUsingKeyboard={isUsingKeyboard}>
                  {optionLabel}
                </Radio>
              </div>
            );
          })}
        </RadioGroup>
      </HorizontalFormGroup>
    );
  }
);

export const ControlledFieldRadioToggle = (
  props: ControlledFieldRadioToggleProps
) => (
  <FieldController<FieldRadioToggleProps, HTMLInputElement>
    as={FieldRadioToggle}
    {...props}
  />
);
